import React, { useEffect } from "react";

/**
 * Loader – a lightweight, accessible, full-viewport loader.
 *
 * Features
 * - Covers the entire screen (fixed overlay).
 * - Minimal spinner using Tailwind + native CSS animation.
 * - Optional message below the spinner.
 * - Prevents background scrolling while visible.
 * - Accessible (role="status", aria-busy, aria-live).
 *
 */

export type LoaderProps = {
  /** Optional text shown under the spinner */
  message?: string;
  /** Diameter of the spinner (px). Default: 56 */
  spinnerSize?: number;
  /** Extra classes for the overlay */
  className?: string;
  /** Extra classes for the spinner ring */
  spinnerClassName?: string;
  /** If true, adds a subtle backdrop blur */
  blur?: boolean;
  /** If true, darkens the background. Default: true */
  withBackdrop?: boolean;
  /** Accessible label for screen readers */
  ariaLabel?: string;
};

const Loader: React.FC<LoaderProps> = ({
  message = "Loading…",
  spinnerSize = 56,
  className = "",
  spinnerClassName = "",
  blur = true,
  withBackdrop = false,
  ariaLabel = "Loading",
}) => {
  // Prevent background scroll while the loader is mounted
  useEffect(() => {
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, []);

  return (
    <div
      className={[
        "fixed inset-0 z-[9999] flex items-center justify-center opacity-50",
        withBackdrop ? "bg-black/40" : "bg-[#CCCCCC]/50",
        blur ? "backdrop-blur-sm" : "",
        className,
      ].join(" ")}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={ariaLabel}
    >
      <div className="flex flex-col items-center gap-4 p-6 select-none">
        {/* Spinner */}
        <div
          className={[
            "animate-spin rounded-full border-4 border-current border-t-transparent",
            "shadow-sm",
            spinnerClassName,
          ].join(" ")}
          style={{ width: spinnerSize, height: spinnerSize }}
        />

        {message ? (
          <div
            className={`text-sm md:text-base ${
              withBackdrop ? "text-white/90" : "text-black/90"
            } drop-shadow-sm`}
          >
            {message}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Loader;
